export enum Role {
  NONE = 0,
  RESPONDENT = 5,
  FACILITATOR = 8,
  ORG_VIEWER = 10,
  ORG_EDITOR = 20,
  ORG_ADMIN = 30,
  SUPERADMIN = 100,
  SERVICE = 200,
}

export const roleToNameMap: Record<Role, string> = {
  [Role.NONE]: "Inactive",
  [Role.RESPONDENT]: "Respondent",
  [Role.FACILITATOR]: "Facilitator",
  [Role.ORG_VIEWER]: "Organization Viewer",
  [Role.ORG_EDITOR]: "Organization Editor",
  [Role.ORG_ADMIN]: "Organization Admin",
  [Role.SUPERADMIN]: "Super Admin",
  [Role.SERVICE]: "Service",
};

type RoleDefinition = {
  label: string;
  shortDescription: string;
  details: string[];
};

export const roleDefinitions: Record<Role, RoleDefinition> = {
  [Role.NONE]: {
    label: "Inactive",
    shortDescription: "No access to the platform. Use for placeholder or deactivated memberships.",
    details: ["No access to the platform.", "Use for placeholder or deactivated memberships."],
  },
  [Role.RESPONDENT]: {
    label: "Respondent",
    shortDescription:
      "Can participate in Collective Discovery sessions they are explicitly invited to. Sees only the Canvas for those sessions. Cannot create Discoveries.",
    details: [
      "Can participate in Collective Discovery sessions they are explicitly invited to.",
      "Sees only the Canvas for those sessions.",
      "Cannot create Discoveries.",
    ],
  },
  [Role.FACILITATOR]: {
    label: "Facilitator",
    shortDescription:
      "Can create and run Collective Discovery sessions for their own team or group. Manages their own sessions and any they are explicitly added to. Does not have access to other teams' Discoveries by default.",
    details: [
      "Can create and run Collective Discovery sessions for their own team or group.",
      "Manages their own sessions and any they are explicitly added to.",
      "Does not have access to other teams' Discoveries by default.",
    ],
  },
  [Role.ORG_VIEWER]: {
    label: "Organization Viewer",
    shortDescription:
      "Has visibility across all Discoveries in the organization. Can create new Discoveries. Cannot edit Discoveries created by others unless explicitly added as an editor.",
    details: [
      "Has visibility across all Discoveries in the organization.",
      "Can create new Discoveries.",
      "Cannot edit Discoveries created by others unless explicitly added as an editor.",
    ],
  },
  [Role.ORG_EDITOR]: {
    label: "Organization Editor",
    shortDescription:
      "Can view and edit all Discoveries across the organization. Intended for people managing the organization's Discovery programme.",
    details: [
      "Can view and edit all Discoveries across the organization.",
      "Intended for people managing the organization's Discovery programme.",
    ],
  },
  [Role.ORG_ADMIN]: {
    label: "Organization Admin",
    shortDescription: "Full organizational access. Manages users, organization settings, and all Discoveries.",
    details: ["Full organizational access.", "Manages users, organization settings, and all Discoveries."],
  },
  [Role.SUPERADMIN]: {
    label: "Super Admin",
    shortDescription: "Can manage the platform across all organizations.",
    details: ["Can administer the system across organizations."],
  },
  [Role.SERVICE]: {
    label: "Service",
    shortDescription: "Reserved for internal system services.",
    details: ["Internal system role used by services."],
  },
};

export const organizationAssignableRoles = [
  Role.NONE,
  Role.RESPONDENT,
  Role.FACILITATOR,
  Role.ORG_VIEWER,
  Role.ORG_EDITOR,
  Role.ORG_ADMIN,
];

export const discoveryProjectAssignableRoles = [Role.ORG_VIEWER, Role.ORG_EDITOR, Role.ORG_ADMIN];
